import { useLogto } from "@logto/react";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useSession } from "src/auth/useSession";
import { ROUTES } from "src/routes/paths";

interface SignOutButtonProps {
	/** True when DXM SSO is configured, so the SSO session ends too (needs the LogtoProvider). */
	withDxmSso: boolean;
	/** Render as an item of an account menu instead of a toolbar button. */
	asMenuItem?: boolean;
}

interface SignOutControlProps {
	asMenuItem?: boolean;
	onClick: () => void;
}

const SignOutControl: FC<SignOutControlProps> = ({ asMenuItem, onClick }) => {
	const { t } = useTranslation();
	return asMenuItem ? (
		<MenuItem onClick={onClick}>{t("auth.signOut")}</MenuItem>
	) : (
		<Button color="inherit" onClick={onClick}>
			{t("auth.signOut")}
		</Button>
	);
};

const DxmSsoSignOutButton: FC<Pick<SignOutButtonProps, "asMenuItem">> = ({ asMenuItem }) => {
	const { signOut } = useSession();
	const navigate = useNavigate();
	const { isAuthenticated, signOut: signOutOfDxmSso } = useLogto();

	const handleClick = useCallback(async () => {
		// Leave the page first: once signed out the shell unmounts, and this component with it.
		await navigate(ROUTES.home, { replace: true });
		await signOut();
		// Without this the next "Sign in" silently re-uses the DXM SSO session (it redirects back to the origin).
		if (isAuthenticated) await signOutOfDxmSso(window.location.origin);
	}, [isAuthenticated, navigate, signOut, signOutOfDxmSso]);

	return <SignOutControl asMenuItem={asMenuItem} onClick={handleClick} />;
};

const SessionOnlySignOutButton: FC<Pick<SignOutButtonProps, "asMenuItem">> = ({ asMenuItem }) => {
	const { signOut } = useSession();
	const navigate = useNavigate();
	// The next person to sign in starts on the Launchpad, not on the previous user's page. Navigate
	// first: once signed out the shell unmounts, and this component with it.
	const handleClick = useCallback(async () => {
		await navigate(ROUTES.home, { replace: true });
		await signOut();
	}, [navigate, signOut]);

	return <SignOutControl asMenuItem={asMenuItem} onClick={handleClick} />;
};

export const SignOutButton: FC<SignOutButtonProps> = ({ withDxmSso, asMenuItem }) =>
	withDxmSso ? <DxmSsoSignOutButton asMenuItem={asMenuItem} /> : <SessionOnlySignOutButton asMenuItem={asMenuItem} />;
